import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import api from "../api";
import { Practice, Coordinator } from "../../types";
import { asArray, isRecord } from "../payloadGuards";
import { RootState } from "../index";
import { normalizePracticeFromApi, normalizePracticeListFromApi } from "../../utils/practiceApiNormalize";

// Types
interface PracticeState {
    practices: Practice[];
    leads: any[];
    practice: Practice | null;
    coordinators: Coordinator[];
    selectedPractice: Practice | null;
    isLoading: boolean;
    status: 'idle' | 'loading' | 'succeeded' | 'failed';
    error: string | null;
}

export interface UpdatePracticeStatusPayload {
    practiceId: string;
    status: 'active' | 'inactive' | 'pending' | 'suspended';
}

export interface SimplePracticeIntegrationPayload {
    practiceId: string;
    simple_practice_api_key: string;
    simple_practice_email: string;
}

export interface PracticeStatusState {
    isLoading: boolean;
    error: string | null;
    lastUpdated: string | null;
}

// Initial state
const initialState: PracticeState = {
    practices: [],
    leads: [],
    practice: null,
    coordinators: [],
    selectedPractice: null,
    isLoading: false,
    status: 'idle',
    error: null,
};

// Helper functions
const handleApiError = (error: unknown): string => {
    if (error instanceof Error) {
        return error.message;
    }
    if (typeof error === 'string') {
        return error;
    }
    return 'An unexpected error occurred';
};

const createAsyncThunkWithErrorHandling = <T, P = void>(
    typePrefix: string,
    payloadCreator: (arg: P) => Promise<T>
) => {
    return createAsyncThunk<T, P, { rejectValue: string }>(
        typePrefix,
        async (arg, { rejectWithValue }) => {
            try {
                return await payloadCreator(arg);
            } catch (error) {
                return rejectWithValue(handleApiError(error));
            }
        }
    );
};

// Async thunks
export const fetchPractices = createAsyncThunkWithErrorHandling<Practice[]>(
    'practices/fetchPractices',
    async () => {
        const response = await api.get('/practice');
        return normalizePracticeListFromApi(response.data.body);
    }
);

export const createPractice = createAsyncThunkWithErrorHandling<Practice, Practice>(
    'practices/createPractice',
    async (practice) => {
        const response = await api.post('/practice', practice);
        if (response.data.statusCode === 200 && response.data.body?.body?.id) {
            return normalizePracticeFromApi(
                {
                    ...practice,
                    id: response.data.body.body.id,
                    _id: response.data.body.body.id,
                    createdAt: new Date(response.data.body.body.created_at).toISOString(),
                    updatedAt: new Date(response.data.body.body.created_at).toISOString(),
                },
                practice
            );
        }
        if (response.data.body?.body) {
            return normalizePracticeFromApi(response.data.body.body, practice);
        }
        return normalizePracticeFromApi(response.data.body, practice);
    }
);

export const savePracticeData = createAsyncThunkWithErrorHandling<Practice, Practice>(
    'practices/savePracticeData',
    async (practice) => {
        // Use whichever ID field the backend provided (_id for MongoDB, id for UUID)
        const practiceId = practice.id || (practice as any)._id;
        const response = await api.put(`/practice/${practiceId}`, practice);
        // Normalize the response so locations always come back as PracticeLocation[].
        // Fall back to the payload we sent if the API omits locations in the response.
        return normalizePracticeFromApi(response.data.body ?? response.data, practice);
    }
);

export const fetchPracticeById = createAsyncThunkWithErrorHandling<Practice, string>(
    'practices/fetchPracticeById',
    async (practiceId) => {
        const response = await api.get(`/practice/${practiceId}`);
        return normalizePracticeFromApi(response.data.body);
    }
);

export const updatePracticeStatus = createAsyncThunkWithErrorHandling<Practice, UpdatePracticeStatusPayload>(
    'practices/updateStatus',
    async (payload) => {
        const response = await api.patch(`/practice/${payload.practiceId}`, { status: payload.status, practiceId: payload.practiceId });
        return normalizePracticeFromApi(response.data.body);
    }
);

export const createIntakeCoordinator = createAsyncThunkWithErrorHandling<Coordinator, Coordinator>(
    'practices/createIntakeCoordinator',
    async (coordinator) => {
        const response = await api.post('/intakecoordinator', coordinator);
        return response.data.body;
    }
);

export const fetchCoordinators = createAsyncThunkWithErrorHandling<Coordinator[], string>(
    'practices/fetchCoordinators',
    async (practiceId) => {
        const response = await api.get(`/practice/${practiceId}/intakecoords`);
        // Expecting response.data.body to be an array of coordinators (Coordinator[])
        return response.data.body;
    }
);

export const updateCoordinator = createAsyncThunkWithErrorHandling<Coordinator, { id: string; data: Partial<Coordinator> }>(
    'practices/updateCoordinator',
    async ({ id, data }) => {
        const response = await api.put(`/intakecoordinator/${id}`, data);
        return response.data.body;
    }
);

export const deleteCoordinator = createAsyncThunkWithErrorHandling<string, string>(
    'practices/deleteCoordinator',
    async (coordinatorId) => {
        await api.delete(`/intakecoordinator/${coordinatorId}`);
        return coordinatorId;
    }
);

export const setupSimplePracticeIntegration = createAsyncThunkWithErrorHandling<Practice, SimplePracticeIntegrationPayload>(
    'practices/setupSimplePracticeIntegration',
    async (payload) => {
        const response = await api.post(`/practice/${payload.practiceId}/simplepractice`, {
            api_key: payload.simple_practice_api_key,
            email: payload.simple_practice_email
        });
        return normalizePracticeFromApi(response.data.body);
    }
);

export const testSimplePracticeConnection = createAsyncThunkWithErrorHandling<{ connected: boolean; message: string }, SimplePracticeIntegrationPayload>(
    'practices/testSimplePracticeConnection',
    async (payload) => {
        const response = await api.post(`/practice/${payload.practiceId}/simplepractice/test`, {
            api_key: payload.simple_practice_api_key,
            email: payload.simple_practice_email
        });
        return response.data.body;
    }
);

export const disconnectSimplePractice = createAsyncThunkWithErrorHandling<Practice, string>(
    'practices/disconnectSimplePractice',
    async (practiceId) => {
        const response = await api.delete(`/practice/${practiceId}/simplepractice`);
        return normalizePracticeFromApi(response.data.body);
    }
);

/** Unwrap API Gateway / Lambda shapes and return a leads array */
function normalizePracticeLeadsPayload(raw: unknown): unknown[] {
    const parseMaybeJson = (value: unknown): unknown => {
        if (typeof value !== 'string') return value;
        try {
            return JSON.parse(value);
        } catch {
            return value;
        }
    };

    let data: unknown = parseMaybeJson(raw);

    // Unwrap { body: ... } once or twice (proxy wrappers)
    for (let i = 0; i < 2; i++) {
        if (data && typeof data === 'object' && 'body' in (data as object)) {
            data = parseMaybeJson((data as { body: unknown }).body);
        }
    }

    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object') {
        const o = data as Record<string, unknown>;
        if (Array.isArray(o.leads)) return o.leads;
        if (Array.isArray(o.data)) return o.data;
        if (Array.isArray(o.items)) return o.items;
    }
    return [];
}

export const fetchPracticeLeads = createAsyncThunkWithErrorHandling<unknown[], string>(
    'practices/fetchPracticeLeads',
    async (practiceId) => {
        const response = await api.get(`/practice/${practiceId}/lead`);
        return normalizePracticeLeadsPayload(response.data?.body ?? response.data);
    }
);

// Common state handlers
const setPending = (state: PracticeState) => {
    state.status = 'loading';
    state.isLoading = true;
    state.error = null;
};

const setFulfilled = (state: PracticeState) => {
    state.status = 'succeeded';
    state.isLoading = false;
    state.error = null;
};

const setRejected = (state: PracticeState, action: PayloadAction<string>) => {
    state.status = 'failed';
    state.isLoading = false;
    state.error = action.payload;
};

const setRejectedAsync = (state: PracticeState, action: any) => {
    state.status = 'failed';
    state.isLoading = false;
    state.error = action.payload || 'An error occurred';
};

// Slice
const practiceSlice = createSlice({
    name: 'practices',
    initialState,
    reducers: {
        setPractice: (state, action: PayloadAction<Practice>) => {
            state.selectedPractice = action.payload;
        },
        clearPractice: (state) => {
            state.selectedPractice = null;
        },
        clearError: (state) => {
            state.error = null;
        },
        resetStatus: (state) => {
            state.status = 'idle';
            state.error = null;
        },
        addCoordinatorToState: (state, action: PayloadAction<Coordinator>) => {
            if (isRecord(action.payload)) state.coordinators.push(action.payload);
        },
        updateCoordinatorInState: (state, action: PayloadAction<Coordinator>) => {
            const index = state.coordinators.findIndex(c => c.id === action.payload.id);
            if (index !== -1) {
                state.coordinators[index] = action.payload;
            }
        },
        removeCoordinatorFromState: (state, action: PayloadAction<string>) => {
            state.coordinators = state.coordinators.filter(c => c.id !== action.payload);
        }
    },
    extraReducers: (builder) => {
        // Fetch practices
        builder
            .addCase(fetchPractices.pending, setPending)
            .addCase(fetchPractices.fulfilled, (state, action) => {
                setFulfilled(state);
                state.practices = asArray(action.payload);
            })
            .addCase(fetchPractices.rejected, setRejectedAsync);

        // Create practice
        builder
            .addCase(createPractice.pending, setPending)
            .addCase(createPractice.fulfilled, (state, action) => {
                setFulfilled(state);
                state.practice = action.payload;
                if (isRecord(action.payload)) state.practices.push(action.payload);
            })
            .addCase(createPractice.rejected, setRejectedAsync);

        // Save practice data
        builder
            .addCase(savePracticeData.pending, setPending)
            .addCase(savePracticeData.fulfilled, (state, action) => {
                setFulfilled(state);
                state.selectedPractice = action.payload;
                state.practice = action.payload;
                const index = state.practices.findIndex(p => p.id === action.payload.id);
                if (index !== -1) {
                    state.practices[index] = action.payload;
                }
            })
            .addCase(savePracticeData.rejected, setRejectedAsync);

        // Fetch practice by ID
        builder
            .addCase(fetchPracticeById.pending, setPending)
            .addCase(fetchPracticeById.fulfilled, (state, action) => {
                setFulfilled(state);
                state.selectedPractice = action.payload;
                state.practice = action.payload;
            })
            .addCase(fetchPracticeById.rejected, setRejectedAsync);

        // Update practice status
        builder
            .addCase(updatePracticeStatus.pending, setPending)
            .addCase(updatePracticeStatus.fulfilled, (state, action) => {
                setFulfilled(state);
                const updatedPractice = action.payload;
                const index = state.practices.findIndex(p => p.id === updatedPractice.id);
                if (index !== -1) {
                    state.practices[index] = updatedPractice;
                }
                if (state.selectedPractice?.id === updatedPractice.id) {
                    state.selectedPractice = updatedPractice;
                }
            })
            .addCase(updatePracticeStatus.rejected, setRejectedAsync);

        // Create intake coordinator
        builder
            .addCase(createIntakeCoordinator.pending, setPending)
            .addCase(createIntakeCoordinator.fulfilled, (state, action) => {
                setFulfilled(state);
                if (isRecord(action.payload)) state.coordinators.push(action.payload);
            })
            .addCase(createIntakeCoordinator.rejected, setRejectedAsync);

        // Fetch coordinators
        builder
            .addCase(fetchCoordinators.pending, setPending)
            .addCase(fetchCoordinators.fulfilled, (state, action) => {
                setFulfilled(state);
                state.coordinators = asArray(action.payload);
            })
            .addCase(fetchCoordinators.rejected, setRejectedAsync);

        // Update coordinator
        builder
            .addCase(updateCoordinator.pending, setPending)
            .addCase(updateCoordinator.fulfilled, (state, action) => {
                setFulfilled(state);
                const index = state.coordinators.findIndex(c => c.id === action.payload.id);
                if (index !== -1) {
                    state.coordinators[index] = action.payload;
                }
            })
            .addCase(updateCoordinator.rejected, setRejectedAsync);

        // Delete coordinator
        builder
            .addCase(deleteCoordinator.pending, setPending)
            .addCase(deleteCoordinator.fulfilled, (state, action) => {
                setFulfilled(state);
                state.coordinators = state.coordinators.filter(c => c.id !== action.payload);
            })
            .addCase(deleteCoordinator.rejected, setRejectedAsync);

        // Setup SimplePractice Integration
        builder
            .addCase(setupSimplePracticeIntegration.pending, setPending)
            .addCase(setupSimplePracticeIntegration.fulfilled, (state, action) => {
                setFulfilled(state);
                state.selectedPractice = action.payload;
                const index = state.practices.findIndex(p => p.id === action.payload.id);
                if (index !== -1) {
                    state.practices[index] = action.payload;
                }
            })
            .addCase(setupSimplePracticeIntegration.rejected, setRejectedAsync);

        // Test SimplePractice Connection
        builder
            .addCase(testSimplePracticeConnection.pending, setPending)
            .addCase(testSimplePracticeConnection.fulfilled, (state) => {
                setFulfilled(state);
                // No state change necessary here
            })
            .addCase(testSimplePracticeConnection.rejected, setRejectedAsync);

        // Fetch Practice Leads
        builder
            .addCase(fetchPracticeLeads.pending, setPending)
            .addCase(fetchPracticeLeads.fulfilled, (state, action) => {
                setFulfilled(state);
                state.leads = asArray(action.payload);
            })
            .addCase(fetchPracticeLeads.rejected, setRejectedAsync);

        // Disconnect SimplePractice
        builder
            .addCase(disconnectSimplePractice.pending, setPending)
            .addCase(disconnectSimplePractice.fulfilled, (state, action) => {
                setFulfilled(state);
                state.selectedPractice = action.payload;
                const index = state.practices.findIndex(p => p.id === action.payload.id);
                if (index !== -1) {
                    state.practices[index] = action.payload;
                }
            })
            .addCase(disconnectSimplePractice.rejected, setRejectedAsync);
    },
});

// Actions
export const {
    setPractice,
    clearPractice,
    clearError,
    resetStatus,
    addCoordinatorToState,
    updateCoordinatorInState,
    removeCoordinatorFromState
} = practiceSlice.actions;

// Selectors
export const selectPractices = (state: RootState) => state.practices.practices;
export const selectSelectedPractice = (state: RootState) => state.practices.selectedPractice;
export const selectCoordinators = (state: RootState) => state.practices.coordinators;
export const selectPracticeLoadingStatus = (state: RootState) => ({
    isLoading: state.practices.isLoading,
    status: state.practices.status,
    error: state.practices.error,
});

export const selectPracticeById = (practiceId: string) => (state: RootState) =>
    state.practices.practices.find(practice => practice.id === practiceId);

export const selectCoordinatorsByPractice = (practiceId: string) => (state: RootState) =>
    state.practices.coordinators.filter(coordinator => coordinator.practiceId === practiceId);

export const selectActivePractices = (state: RootState) =>
    state.practices.practices.filter(practice => practice.status === 'active');

export default practiceSlice.reducer;